// frontend/components/features/CredentialUpload.tsx
"use client";

import { useState, useRef, type DragEvent, type ChangeEvent, type FormEvent } from "react";
import { Upload, X, FileText, AlertCircle, Loader2, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  uploadCredential,
  useCredentialTypes,
  type CredentialUploadData,
} from "@/lib/hooks/useNurseCredentials";
import { useToast } from "@/components/providers/ToastProvider";

// =============================================================================
// Constants
// =============================================================================

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ALLOWED_FILE_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"];
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// =============================================================================
// Helper Functions
// =============================================================================

function isValidFileType(file: File): boolean {
  const extension = "." + file.name.split(".").pop()?.toLowerCase();
  return (
    ALLOWED_FILE_TYPES.includes(file.type) ||
    ALLOWED_FILE_EXTENSIONS.includes(extension)
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

// =============================================================================
// Component Props
// =============================================================================

export interface CredentialUploadProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function CredentialUpload({ onSuccess, onCancel }: CredentialUploadProps) {
  const { showToast } = useToast();
  const { credentialTypes, isLoading: typesLoading } = useCredentialTypes();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<CredentialUploadData>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // =============================================================================
  // File Handling
  // =============================================================================

  const validateFile = (file: File): string | null => {
    if (!isValidFileType(file)) {
      return `Invalid file type. Allowed: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`;
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    setFileError("");
    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =============================================================================
  // Form Handling
  // =============================================================================

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!selectedFile) {
      errors.file = "Please select a file to upload";
    }

    if (!formData.credential_type_id) {
      errors.credential_type_id = "Credential type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm() || !selectedFile || !formData.credential_type_id) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await uploadCredential(
        selectedFile,
        formData as CredentialUploadData,
        (progress) => setUploadProgress(progress)
      );

      showToast({
        variant: "success",
        title: "Credential uploaded",
        description: "Your credential has been uploaded and is pending verification.",
      });

      // Reset form
      setSelectedFile(null);
      setFormData({});
      setFormErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Call success callback
      onSuccess?.();
    } catch (error) {
      showToast({
        variant: "error",
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload credential",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* File Upload Zone */}
      <div>
        <label className="block text-sm font-medium text-slate-900 mb-2">
          Document File *
        </label>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            isDragging && "border-slate-400 bg-slate-50",
            !isDragging && !fileError && "border-slate-300 hover:border-slate-400 hover:bg-slate-50",
            fileError && "border-red-300 bg-red-50"
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_FILE_EXTENSIONS.join(",")}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={isUploading}
          />

          {!selectedFile ? (
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-full bg-slate-100 p-3">
                <Upload className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  PDF, JPG, PNG, DOC, DOCX (max {MAX_FILE_SIZE_MB}MB)
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <FileText className="h-6 w-6 text-slate-600" />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-slate-900">{selectedFile.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                className="rounded-full p-1 hover:bg-slate-200 transition-colors"
                disabled={isUploading}
              >
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>
          )}
        </div>

        {fileError && (
          <div className="mt-2 flex items-start gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{fileError}</span>
          </div>
        )}
        {formErrors.file && (
          <div className="mt-2 flex items-start gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{formErrors.file}</span>
          </div>
        )}
      </div>

      {/* Credential Type */}
      <div>
        <label htmlFor="credential_type_id" className="block text-sm font-medium text-slate-900 mb-2">
          Credential Type *
        </label>
        <select
          id="credential_type_id"
          value={formData.credential_type_id || ""}
          onChange={(e) =>
            setFormData({ ...formData, credential_type_id: Number(e.target.value) })
          }
          disabled={isUploading || typesLoading}
          className={cn(
            "w-full rounded-lg border px-3 py-2 text-sm transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent",
            formErrors.credential_type_id ? "border-red-300" : "border-slate-300"
          )}
        >
          <option value="">Select credential type...</option>
          {credentialTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        {formErrors.credential_type_id && (
          <p className="mt-1 text-sm text-red-600">{formErrors.credential_type_id}</p>
        )}
      </div>

      {/* License/Certificate Number */}
      <div>
        <label htmlFor="license_number" className="block text-sm font-medium text-slate-900 mb-2">
          License/Certificate Number
        </label>
        <input
          type="text"
          id="license_number"
          value={formData.license_number || ""}
          onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
          disabled={isUploading}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          placeholder="e.g., RN123456"
        />
      </div>

      {/* Issuing State */}
      <div>
        <label htmlFor="issuing_state" className="block text-sm font-medium text-slate-900 mb-2">
          Issuing State
        </label>
        <input
          type="text"
          id="issuing_state"
          value={formData.issuing_state || ""}
          onChange={(e) => setFormData({ ...formData, issuing_state: e.target.value.toUpperCase() })}
          disabled={isUploading}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          placeholder="e.g., CA, NY, TX"
          maxLength={2}
        />
      </div>

      {/* Issuing Authority */}
      <div>
        <label htmlFor="issuing_authority" className="block text-sm font-medium text-slate-900 mb-2">
          Issuing Authority
        </label>
        <input
          type="text"
          id="issuing_authority"
          value={formData.issuing_authority || ""}
          onChange={(e) => setFormData({ ...formData, issuing_authority: e.target.value })}
          disabled={isUploading}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          placeholder="e.g., State Board of Nursing"
        />
      </div>

      {/* Issue Date */}
      <div>
        <label htmlFor="issued_at" className="block text-sm font-medium text-slate-900 mb-2">
          Issue Date
        </label>
        <input
          type="date"
          id="issued_at"
          value={formData.issued_at || ""}
          onChange={(e) => setFormData({ ...formData, issued_at: e.target.value })}
          disabled={isUploading}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
        />
      </div>

      {/* Expiration Date */}
      <div>
        <label htmlFor="expires_at" className="block text-sm font-medium text-slate-900 mb-2">
          Expiration Date
        </label>
        <input
          type="date"
          id="expires_at"
          value={formData.expires_at || ""}
          onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
          disabled={isUploading}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
        />
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="h-4 w-4 animate-spin text-slate-600" />
            <span className="text-sm font-medium text-slate-900">
              Uploading... {uploadProgress}%
            </span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-slate-900 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isUploading}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isUploading || !selectedFile}
          className={cn(
            "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
            "bg-slate-900 text-white hover:bg-slate-800",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              Upload Credential
            </>
          )}
        </button>
      </div>
    </form>
  );
}
